<script setup lang="ts">
import { computed } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { twoKindSelectionText, type BulkBarScheduleSelection } from './bulkSelectionText';
import type { BulkSelectionMode } from '@/composables/useBulkSelection';

/**
 * The frame of the bar above the search results, and its selection row.
 *
 * The selection row reads "N selected", "All N matching", or "N of M matching" once rows are
 * unticked. It has the clear button, and "Select all N matching" in small text. On the custom list
 * search it says what is selected of each kind, such as "3 lists, 14 schedules selected", and offers
 * "Select all matching 40 custom lists / 112 schedules", where each count is its own link.
 *
 * It holds no bulk action and no run. `BulkActionBar` draws it alone for an entity without bulk
 * actions. `BulkActionBarWithActions` puts its buttons in the `actions` slot, and its run below.
 */
const props = withDefaults(defineProps<{
  /** False while the page wants the selection kept out of the bar, such as after a plain click. */
  showSelection?: boolean;
  mode: BulkSelectionMode;
  count: number;
  /** Rows unticked after "select all matching". */
  excludedCount?: number;
  /** The pager's total for the search on screen. */
  total?: number;
  /** Whether "Select all N matching" would add rows to the selection. */
  canSelectAllMatching?: boolean;
  /** The schedule selection, on the custom list search only. */
  schedules?: BulkBarScheduleSelection | null;
  /** True when the default slot has something to show without a selection, such as a run. */
  hasOtherContent?: boolean;
}>(), { showSelection: true, excludedCount: 0, total: 0, canSelectAllMatching: false, schedules: null, hasOtherContent: false });

const emit = defineEmits<{
  (e: 'clear'): void;
  (e: 'select-all-matching'): void;
  /** "112 schedules" was clicked: select every schedule that passes the search. */
  (e: 'select-all-matching-schedules'): void;
}>();

/** Whether anything of any kind is ticked. The selection row shows from the first ticked list or schedule. */
const hasAnySelection = computed(() => props.count > 0 || (props.schedules?.count ?? 0) > 0);
/** Whether the selection row shows: something is selected, and the page wants it in the bar. */
const showsSelectionRow = computed(() => props.showSelection && hasAnySelection.value);
const isVisible = computed(() => showsSelectionRow.value || props.hasOtherContent);

// After "select all matching" with rows unticked, the bar says how many of the matches are
// selected, so "All 104 matching" never hides the row the user took out.
const selectionText = computed(() => {
  if (props.schedules) {
    return twoKindSelectionText([
      { mode: props.mode, count: props.count, excludedCount: props.excludedCount, singular: 'list', plural: 'lists' },
      { ...props.schedules, singular: 'schedule', plural: 'schedules' },
    ]);
  }
  if (props.mode !== 'search') return `${props.count.toLocaleString()} selected`;
  if (props.excludedCount > 0) {
    return `${props.count.toLocaleString()} of ${(props.count + props.excludedCount).toLocaleString()} matching`;
  }
  return `All ${props.count.toLocaleString()} matching`;
});

/**
 * The schedule link's text in "Select all matching 40 custom lists / 112 schedules". While the
 * count is on its way it reads "… schedules", never "0 schedules", which would look like an answer.
 */
const scheduleLinkText = computed(() => {
  const schedules = props.schedules;
  if (!schedules) return '';
  if (schedules.total !== null) return `${schedules.total.toLocaleString()} schedules`;
  return schedules.totalFailed ? "schedules (couldn't count them)" : '… schedules';
});

/** The two-link offer shows while at least one of its links would add something. */
const showsTwoKindOffer = computed(
  () => props.schedules !== null && (props.canSelectAllMatching || props.schedules.canSelectAllMatching || props.schedules.total === null),
);
</script>

<template>
  <div
    v-if="isVisible"
    class="bg-background/95 supports-[backdrop-filter]:bg-background/80 border-border/80 sticky top-0 z-10 mx-3 mt-3 flex flex-col gap-2 rounded-lg border px-3 py-2 backdrop-blur"
    data-testid="bulk-action-bar"
  >
    <div v-if="showsSelectionRow" class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="text-muted-foreground hover:text-foreground hover:bg-muted/40 border-border/60 inline-flex size-6 shrink-0 items-center justify-center rounded-md border"
        data-testid="bulk-action-clear"
        aria-label="Clear selection"
        @click="emit('clear')"
      >
        <XMarkIcon class="size-3.5" aria-hidden="true" />
      </button>

      <span class="text-foreground text-sm font-medium" data-testid="bulk-action-count">{{ selectionText }}</span>
      <span
        v-if="showsTwoKindOffer && schedules"
        class="text-muted-foreground text-xs"
        data-testid="bulk-select-all-matching-row"
      >
        Select all matching
        <button
          type="button"
          class="hover:text-foreground underline-offset-2 hover:underline disabled:no-underline disabled:opacity-70"
          :disabled="!canSelectAllMatching"
          data-testid="bulk-select-all-matching-lists"
          @click="emit('select-all-matching')"
        >{{ total.toLocaleString() }} custom lists</button>
        /
        <button
          type="button"
          class="hover:text-foreground underline-offset-2 hover:underline disabled:no-underline disabled:opacity-70"
          :disabled="!schedules.canSelectAllMatching"
          data-testid="bulk-select-all-matching-schedules"
          @click="emit('select-all-matching-schedules')"
        >{{ scheduleLinkText }}</button>
      </span>
      <button
        v-else-if="canSelectAllMatching && !schedules"
        type="button"
        class="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
        data-testid="bulk-select-all-matching"
        @click="emit('select-all-matching')"
      >
        Select all {{ total.toLocaleString() }} matching
      </button>

      <slot name="actions" />
    </div>

    <slot />
  </div>
</template>
